import { api } from './api';
import { planeConfig } from './plane-config';

/* GitHub and Google sign-in, through Clerk. Clerk's browser library is
   loaded from the instance's own Frontend API (clerk.offstage.cloud), and only
   on the pages that sign in, the way Clerk's own SDKs load it. It does the
   OAuth round trip; the plane then trades the Clerk session for its own
   (POST /v1/auth/clerk) and the Clerk session is signed out at once: from
   there on the console is signed in to the plane, not to Clerk. */

export type SocialProvider = 'github' | 'google';

/** The slice of clerk-js (v6) the console uses. */
interface ClerkBrowser {
  load(options?: Record<string, unknown>): Promise<void>;
  client?: {
    signIn: {
      authenticateWithRedirect(params: { strategy: string; redirectUrl: string; redirectUrlComplete: string }): Promise<void>;
    };
  };
  session?: { getToken(): Promise<string | null> } | null;
  handleRedirectCallback(params: Record<string, string>, navigate?: (to: string) => Promise<unknown>): Promise<unknown>;
  signOut(): Promise<void>;
}

declare global {
  interface Window {
    Clerk?: ClerkBrowser;
  }
}

const CLERK_JS_MAJOR = 6;

export interface SocialSignIn {
  /** The plane's Clerk publishable key, or null when it offers email sign-in alone. */
  publishableKey: string | null;
  /** The buttons to show, in order; empty when publishableKey is null. */
  providers: SocialProvider[];
}

/** What the plane offers besides email. Asked once. */
export async function socialSignIn(): Promise<SocialSignIn> {
  const answer = await planeConfig();
  return answer === null || answer.clerkPublishableKey === null
    ? { publishableKey: null, providers: [] }
    : { publishableKey: answer.clerkPublishableKey, providers: answer.socialProviders };
}

/* pk_live_<base64 of "clerk.offstage.cloud$">: the host the library is served from. */
function frontendApi(publishableKey: string): string {
  const host = atob(publishableKey.replace(/^pk_(live|test)_/, '')).replace(/\$$/, '');
  if (!/^[a-z0-9.-]+$/i.test(host)) throw new Error('that is not a Clerk publishable key');
  return host;
}

let loading: Promise<ClerkBrowser> | undefined;

async function clerk(): Promise<ClerkBrowser> {
  loading ??= (async () => {
    const { publishableKey } = await socialSignIn();
    if (publishableKey === null) throw new Error('GitHub and Google sign-in are not set up here');
    if (window.Clerk === undefined) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://${frontendApi(publishableKey)}/npm/@clerk/clerk-js@${CLERK_JS_MAJOR}/dist/clerk.browser.js`;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.dataset.clerkPublishableKey = publishableKey;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Clerk could not be reached'));
        document.head.append(script);
      });
    }
    const instance = window.Clerk;
    if (instance === undefined) throw new Error('Clerk did not load');
    await instance.load();
    return instance;
  })();
  loading.catch(() => {
    loading = undefined;
  });
  return loading;
}

/** Off to GitHub or Google; the browser comes back to /sso-callback. */
export async function continueWith(provider: SocialProvider, next: string): Promise<void> {
  const instance = await clerk();
  /* A Clerk session left by a sign-in that stopped halfway would refuse a new one. */
  if (instance.session) await instance.signOut();
  const back = `${window.location.origin}/sso-callback?next=${encodeURIComponent(next)}`;
  await instance.client!.signIn.authenticateWithRedirect({ strategy: `oauth_${provider}`, redirectUrl: back, redirectUrlComplete: back });
}

/**
 * Back from GitHub or Google: finishes Clerk's side, then hands its session
 * token to the plane. Clerk is signed out either way. `created` says whether
 * the plane made a new account.
 */
export async function finishSocialSignIn(): Promise<{ created: boolean; email: string }> {
  const instance = await clerk();
  try {
    /* Clerk navigates when it is done; the console does that itself, so every target is ignored. */
    await instance.handleRedirectCallback(
      { signInUrl: '/sign-in', signUpUrl: '/sign-up', signInForceRedirectUrl: '/sso-callback', signUpForceRedirectUrl: '/sso-callback', continueSignUpUrl: '/sign-in' },
      async () => undefined,
    );
    const token = instance.session ? await instance.session.getToken() : null;
    if (token === null) throw new Error('the sign-in with GitHub or Google did not finish');
    const answer = await api<{ created: boolean; user: { email: string } }>('/v1/auth/clerk', { method: 'POST', body: { token } });
    return { created: answer.created, email: answer.user.email };
  } finally {
    await instance.signOut().catch(() => undefined);
  }
}
